/**
 * localStorage Cleanup Utility
 *
 * Cleans up old localStorage entries to prevent QuotaExceededError.
 * This addresses Sentry issue XLINX-PRODUCTION-80D where VueOfflineStorage
 * fails due to localStorage being full.
 */

const ROUTE_KEY_PREFIX = 'route-';
const MAX_ROUTE_ENTRIES = 2;
const CACHE_KEY_PREFIX = 'xlinx-http-cache:';
const VUE_OFFLINE_KEYS_KEY = 'VueOfflineStorageKeys';

/**
 * Get all localStorage keys matching a prefix
 */
function getKeysByPrefix(prefix) {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keys.push(key);
        }
    }
    return keys;
}

/**
 * Clean up old route-* entries, keeping only the most recent ones
 */
function cleanupOldRouteEntries() {
    const routeKeys = getKeysByPrefix(ROUTE_KEY_PREFIX);

    if (routeKeys.length <= MAX_ROUTE_ENTRIES) {
        return 0;
    }

    // Sort by route ID (numeric) descending to keep newest
    const sortedKeys = routeKeys.sort((a, b) => {
        const idA = parseInt(a.replace(ROUTE_KEY_PREFIX, ''), 10);
        const idB = parseInt(b.replace(ROUTE_KEY_PREFIX, ''), 10);
        return idB - idA;
    });

    // Remove oldest entries (keep MAX_ROUTE_ENTRIES)
    const keysToRemove = sortedKeys.slice(MAX_ROUTE_ENTRIES);
    keysToRemove.forEach((key) => {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Ignore removal errors
        }
    });

    return keysToRemove.length;
}

/**
 * Clean up HTTP cache entries
 */
function cleanupHttpCache() {
    const cacheKeys = getKeysByPrefix(CACHE_KEY_PREFIX);
    let removed = 0;

    cacheKeys.forEach((key) => {
        try {
            localStorage.removeItem(key);
            removed++;
        } catch (e) {
            // Ignore removal errors
        }
    });

    return removed;
}

/**
 * Clean up VueOfflineStorage entries
 */
function cleanupVueOfflineStorage() {
    let removed = 0;

    try {
        const keysJson = localStorage.getItem(VUE_OFFLINE_KEYS_KEY);
        if (keysJson) {
            const keys = JSON.parse(keysJson);
            keys.forEach((key) => {
                try {
                    localStorage.removeItem(key);
                    removed++;
                } catch (e) {
                    // Ignore
                }
            });
            localStorage.removeItem(VUE_OFFLINE_KEYS_KEY);
        }
    } catch (e) {
        // Ignore parse errors
    }

    return removed;
}

/**
 * Get total localStorage usage in bytes
 */
function getLocalStorageSize() {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
            const value = localStorage.getItem(key);
            total += key.length + (value ? value.length : 0);
        }
    }
    return total;
}

/**
 * Check if localStorage is near quota (> 4MB used)
 */
function isNearQuota() {
    const size = getLocalStorageSize();
    const threshold = 4 * 1024 * 1024; // 4MB
    return size > threshold;
}

/**
 * Emergency cleanup - removes all non-essential data
 */
function emergencyCleanup() {
    // eslint-disable-next-line no-console
    console.warn('[localStorage] Running emergency cleanup due to quota exceeded');

    let removed = 0;
    removed += cleanupHttpCache();
    removed += cleanupVueOfflineStorage();
    removed += cleanupOldRouteEntries();

    // Remove old form data
    const formKeys = getKeysByPrefix('form-');
    formKeys.forEach((key) => {
        try {
            localStorage.removeItem(key);
            removed++;
        } catch (e) {
            // Ignore
        }
    });

    return removed;
}

/**
 * Safe localStorage.setItem that handles QuotaExceededError
 */
function safeSetItem(key, value) {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (e) {
        if (e.name === 'QuotaExceededError' || e.code === 22) {
            emergencyCleanup();
            try {
                localStorage.setItem(key, value);
                return true;
            } catch (retryError) {
                // eslint-disable-next-line no-console
                console.error('[localStorage] Failed to save even after cleanup:', key);
                return false;
            }
        }
        throw e;
    }
}

/**
 * Run periodic cleanup on app initialization
 */
function runStartupCleanup() {
    try {
        // Always clean up old route entries
        const routesRemoved = cleanupOldRouteEntries();
        if (routesRemoved > 0) {
            // eslint-disable-next-line no-console
            console.info(`[localStorage] Cleaned up ${routesRemoved} old route entries`);
        }

        // If near quota, do more aggressive cleanup
        if (isNearQuota()) {
            const cacheRemoved = cleanupHttpCache();
            // eslint-disable-next-line no-console
            console.info(`[localStorage] Cleaned up ${cacheRemoved} cache entries (near quota)`);
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[localStorage] Cleanup failed:', e);
    }
}

export {
    cleanupHttpCache,
    cleanupOldRouteEntries,
    cleanupVueOfflineStorage,
    emergencyCleanup,
    getLocalStorageSize,
    isNearQuota,
    runStartupCleanup,
    safeSetItem,
};
